import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';

/**
 * H-207 — a economia de pontos é configuração publicada com o conteúdo
 * (ADR 0023 decisões 4 e 5), não constante de código.
 *
 * Os valores viajam no manifesto (`content/economia.yml` → compilador →
 * `manifest.json`), pelo mesmo canal das lições: mudar o custo da dica é
 * editar o conteúdo e publicar, sem tocar em `src/`. E porque o estudante é
 * offline-first, o último valor visto persiste no aparelho — em modo de
 * avião vale o que valia da última vez, e sem valor nenhum valem as
 * omissões, que são os números que sempre estiveram no código.
 */

/** RN-007 e RF-040 — os valores históricos, agora como omissão. */
export const ECONOMIA_POR_OMISSAO = {
  /**
   * O custo é pequeno de propósito. Serve para o estudante hesitar um
   * segundo, não para o punir: ficar preso é o único desfecho verdadeiramente
   * mau, e uma dica cara empurra para lá.
   */
  custoDica: 5,
  /** Bónus por completar a lição inteira. */
  bonusLicao: 10,
  /**
   * H-126 — o peso do projecto integrador na barra do nível, em
   * lições-equivalentes. É o único parâmetro do projecto que é configurável
   * (RN-011: a exigência de o ter aprovado não é).
   */
  pesoProjecto: 3,
  /** H-121 — num desafio, a dica custa este múltiplo do custo normal. */
  factorDicaDesafio: 2,
} as const;

interface EstadoDeEconomia {
  custoDica: number;
  bonusLicao: number;
  pesoProjecto: number;
  factorDicaDesafio: number;
  definir: (valores: {
    custoDica: number;
    bonusLicao: number;
    pesoProjecto: number;
    factorDicaDesafio: number;
  }) => void;
}

export const useEconomia = create<EstadoDeEconomia>()(
  persist(
    (set) => ({
      ...ECONOMIA_POR_OMISSAO,
      definir: (valores) => set(valores),
    }),
    { name: 'economia', storage: armazenamento() },
  ),
);

/**
 * Chamada pelo carregador do catálogo quando o manifesto chega. Valores em
 * falta ou tortos caem para a omissão — um manifesto antigo, ou um engano na
 * publicação, nunca deixa a dica de graça nem a lição sem bónus.
 */
export function definirEconomia(economia?: {
  custo_dica?: number;
  bonus_licao?: number;
  peso_projecto?: number;
  factor_dica_desafio?: number;
}): void {
  const valida = (n: unknown, omissao: number, minimo = 0): number =>
    typeof n === 'number' && Number.isInteger(n) && n >= minimo ? n : omissao;
  useEconomia.getState().definir({
    custoDica: valida(economia?.custo_dica, ECONOMIA_POR_OMISSAO.custoDica),
    bonusLicao: valida(economia?.bonus_licao, ECONOMIA_POR_OMISSAO.bonusLicao),
    pesoProjecto: valida(economia?.peso_projecto, ECONOMIA_POR_OMISSAO.pesoProjecto, 1),
    factorDicaDesafio: valida(economia?.factor_dica_desafio, ECONOMIA_POR_OMISSAO.factorDicaDesafio, 1),
  });
}
